// Global Instructions Rule Applied!
//
// Configuration → Fund Statuses: admin CRUD over cfg.fund_statuses — the two
// independent lifecycle clocks on a fund (fundraising and investment). Chrome
// copied from PipelineStatusesPanel, the newest usage-count + replace-flow
// sibling; the list is small (3 + 4), so a category Segmented filter replaces
// pagination.
//
// Three deliberate differences from that sibling:
//
//  1. BEHAVIOUR IS A SINGLE CHOICE ON CREATE. cfg.fund_statuses carries three
//     flags (is_open / is_cancelled / is_terminal) and only four of their eight
//     combinations mean anything. The create modal asks ONE question — Open /
//     Not open / Terminal — and derives all three, so an invalid row cannot be
//     authored through the UI. (The schema's CHECKs stop psql doing it too.)
//     Those labels describe BEHAVIOUR, not a lifecycle position: a closed raise
//     and a not-yet-started investment period carry identical flags, because a
//     raise may reopen and both must stay in pickers.
//  2. BEHAVIOUR IS IMMUTABLE AFTER CREATE. Flipping is_open on an existing row
//     silently re-scopes every fund picker AND the prospect-follower gate,
//     app-wide, with no undo and nothing in the audit trail a user would think
//     to look at. Same posture as the sibling's "pipeline kind is fixed once a
//     status exists".
//  3. is_cancelled IS SYSTEM-OWNED and never offered. Exactly one seeded row
//     carries it, the schema pins it to the fundraising category, and it means
//     something specific — a failed raise, as opposed to funds.termination_date
//     which is a fund that raised and later wound down.
//
// Deleting an in-use status is refused by the backend with a 409 and routed
// into the Replace modal (same-category only — the composite FK on public.funds
// would reject a cross-category move anyway, but the RPC gives a readable
// message instead of constraint 23503 in a toast). After every write the
// global-configs cache refreshes so fund pickers and the Terms tab update
// immediately.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, InputNumber, Modal, Popconfirm, Radio, Segmented, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, SwapOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  FUND_STATUS_CATEGORIES,
  FUND_STATUS_CATEGORY_LABELS,
  type FundStatusAdminRow,
  type FundStatusCategory
} from '../../../state_manager/data_models/fund_statuses_config_model.ts'
import {
  createFundStatus,
  deleteFundStatus,
  fetchFundStatuses,
  reassignFundStatuses,
  StatusInUseError,
  updateFundStatus
} from '../services/fundStatusesConfigService.ts'
import { ReplaceFundStatusModal } from './ReplaceFundStatusModal.tsx'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'

const { Title, Text } = Typography

/** One question on the create modal, three flags out. */
// Named for what the flags MEAN, not for a lifecycle position they cannot
// express. "Closed" fundraising and "Pre-Investment" carry identical flags —
// neither open nor terminal — because a closed raise may reopen and the fund
// must stay in pickers either way. Labelling that pair "Not started" would be
// a claim the data does not support.
type BehaviourKey = 'not_open' | 'open' | 'terminal'

const BEHAVIOUR_FLAGS: Record<BehaviourKey, { is_open: boolean; is_terminal: boolean }> = {
  not_open: { is_open: false, is_terminal: false },
  open: { is_open: true, is_terminal: false },
  terminal: { is_open: false, is_terminal: true }
}

const BEHAVIOUR_LABEL: Record<BehaviourKey, string> = {
  not_open: 'Not open',
  open: 'Open',
  terminal: 'Terminal'
}

const BEHAVIOUR_HINT: Record<BehaviourKey, string> = {
  not_open:
    'The clock is not running, but the fund still appears in pickers — a closed raise you may reopen, or a period not yet begun.',
  open: 'The clock is running — raising, or deploying.',
  terminal: 'Over for good. The fund drops out of the app-wide picker default.'
}

export default function FundStatusesPanel() {
  const [rows, setRows] = useState<FundStatusAdminRow[]>([])
  const [loading, setLoading] = useState(false)
  const [category, setCategory] = useState<FundStatusCategory>('fundraising')

  // Add / rename modal
  const [nameModalOpen, setNameModalOpen] = useState(false)
  const [editing, setEditing] = useState<FundStatusAdminRow | null>(null)
  const [nameValue, setNameValue] = useState('')
  const [sortValue, setSortValue] = useState<number | null>(null)
  const [modalCategory, setModalCategory] = useState<FundStatusCategory>('fundraising')
  const [behaviour, setBehaviour] = useState<BehaviourKey>('open')
  const [submitting, setSubmitting] = useState(false)

  // Replace / merge modal
  const [replaceOpen, setReplaceOpen] = useState(false)
  const [replaceInitialSources, setReplaceInitialSources] = useState<string[]>([])
  const [reassigning, setReassigning] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchFundStatuses(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load fund statuses')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const visibleRows = useMemo(
    () => rows.filter((r) => r.fund_statuses__category === category),
    [rows, category]
  )

  const openAdd = () => {
    setEditing(null)
    setNameValue('')
    setSortValue(null)
    setModalCategory(category)
    setBehaviour('open')
    setNameModalOpen(true)
  }

  const openRename = (row: FundStatusAdminRow) => {
    setEditing(row)
    setNameValue(row.fund_statuses__name)
    setSortValue(row.fund_statuses__sort_order ?? null)
    setModalCategory(row.fund_statuses__category as FundStatusCategory)
    setBehaviour(
      row.fund_statuses__is_open ? 'open' : row.fund_statuses__is_terminal ? 'terminal' : 'not_open'
    )
    setNameModalOpen(true)
  }

  const submitName = async () => {
    const name = nameValue.trim()
    if (!name) return
    setSubmitting(true)
    try {
      if (editing) {
        // Name and ordering only. Behaviour and category are fixed.
        await updateFundStatus(
          editing.fund_statuses__id,
          { name, sort_order: sortValue },
          token()
        )
        toast.success('Status renamed')
      } else {
        await createFundStatus(
          { name, category: modalCategory, sort_order: sortValue, ...BEHAVIOUR_FLAGS[behaviour] },
          token()
        )
        toast.success('Status added')
      }
      setNameModalOpen(false)
      await load()
      refreshBoardConfigs()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async (row: FundStatusAdminRow) => {
    setDeletingId(row.fund_statuses__id)
    try {
      await deleteFundStatus(row.fund_statuses__id, token())
      toast.success('Status deleted')
      await load()
      refreshBoardConfigs()
    } catch (e) {
      if (e instanceof StatusInUseError) {
        setReplaceInitialSources([row.fund_statuses__id])
        setReplaceOpen(true)
      } else {
        showErrorToast(e instanceof Error ? e.message : 'Delete failed')
      }
    } finally {
      setDeletingId(null)
    }
  }

  const onReassign = async (sourceIds: string[], targetId: string, deleteSources: boolean) => {
    setReassigning(true)
    try {
      await reassignFundStatuses(sourceIds, targetId, deleteSources, token())
      toast.success('Funds reassigned')
      setReplaceOpen(false)
      await load()
      refreshBoardConfigs()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Reassign failed')
    } finally {
      setReassigning(false)
    }
  }

  const columns: ColumnsType<FundStatusAdminRow> = [
    {
      title: 'Status',
      dataIndex: 'fund_statuses__name',
      key: 'name',
      sorter: (a, b) => a.fund_statuses__name.localeCompare(b.fund_statuses__name),
      render: (name: string) => <Text>{name}</Text>
    },
    {
      title: 'Key',
      dataIndex: 'fund_statuses__value',
      key: 'value',
      width: 200,
      render: (value: string | null | undefined) =>
        value ? <Text type='secondary' style={{ fontSize: 12 }}>{value}</Text> : <Text type='secondary'>—</Text>
    },
    {
      // Read-only on purpose (see the header). Same colours the fund form uses,
      // so an admin sees exactly what a user will see.
      title: 'Behaviour',
      key: 'behaviour',
      width: 210,
      render: (_, r) => (
        <Space size={4}>
          {r.fund_statuses__is_open ? <Tag color='green'>Open</Tag> : null}
          {r.fund_statuses__is_cancelled ? <Tag>Cancelled</Tag> : null}
          {r.fund_statuses__is_terminal && !r.fund_statuses__is_cancelled ? <Tag>Terminal</Tag> : null}
          {!r.fund_statuses__is_open && !r.fund_statuses__is_terminal ? <Tag color='blue'>Not open</Tag> : null}
        </Space>
      )
    },
    {
      title: 'Used by',
      dataIndex: 'fund_statuses__usage_count',
      key: 'usage',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.fund_statuses__usage_count - b.fund_statuses__usage_count,
      render: (count: number) =>
        count > 0 ? (
          <Tooltip title='Funds whose fundraising or investment status is this value'>
            <Tag>{count} fund{count === 1 ? '' : 's'}</Tag>
          </Tooltip>
        ) : (
          <Text type='secondary'>unused</Text>
        )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <Space size='small'>
          <Tooltip title='Rename'>
            <Button type='text' size='small' icon={<EditOutlined />} onClick={() => openRename(row)} />
          </Tooltip>
          <Popconfirm
            title={
              row.fund_statuses__usage_count > 0
                ? 'This status is in use — deleting opens the Replace flow.'
                : 'Delete this status?'
            }
            okText={row.fund_statuses__usage_count > 0 ? 'Replace…' : 'Delete'}
            onConfirm={() => onDelete(row)}
          >
            <Button
              type='text'
              size='small'
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === row.fund_statuses__id}
            />
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div className='flex flex-col h-full min-h-0' data-component='FundStatusesPanel'>
      <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
        <Title level={4} style={{ margin: 0 }}>
          Fund Statuses
        </Title>
        <Space>
          <Segmented
            value={category}
            onChange={(val) => setCategory(val as FundStatusCategory)}
            options={FUND_STATUS_CATEGORIES.map((c) => ({
              value: c,
              label: FUND_STATUS_CATEGORY_LABELS[c]
            }))}
          />
          <Button
            icon={<SwapOutlined />}
            onClick={() => {
              setReplaceInitialSources([])
              setReplaceOpen(true)
            }}
          >
            Replace / Merge
          </Button>
          <Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>
            New Status
          </Button>
        </Space>
      </div>

      <Text type='secondary' style={{ marginBottom: 8 }}>
        A fund runs two independent clocks. A fund past its initial close but before its final
        close is both raising and investing, so the two are set separately on the fund's Terms tab.
      </Text>

      <div className='flex-1 min-h-0 overflow-auto'>
        <Table
          size='small'
          rowKey='fund_statuses__id'
          columns={columns}
          dataSource={visibleRows}
          loading={loading}
          pagination={false}
        />
      </div>

      <Modal
        title={editing ? 'Rename status' : 'New status'}
        open={nameModalOpen}
        okText={editing ? 'Save' : 'Add'}
        okButtonProps={{ disabled: !nameValue.trim() }}
        confirmLoading={submitting}
        onOk={submitName}
        onCancel={() => setNameModalOpen(false)}
        destroyOnClose
      >
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <div>
            <Text strong>Name</Text>
            <Input
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onPressEnter={submitName}
              placeholder='Status name'
              style={{ marginTop: 4 }}
              autoFocus
            />
          </div>
          <div>
            <Text strong>Clock</Text>
            <Select
              value={modalCategory}
              onChange={(val) => setModalCategory(val as FundStatusCategory)}
              options={FUND_STATUS_CATEGORIES.map((c) => ({
                value: c,
                label: FUND_STATUS_CATEGORY_LABELS[c]
              }))}
              disabled={!!editing}
              style={{ width: '100%', marginTop: 4 }}
            />
            {editing && (
              <Text type='secondary' style={{ fontSize: 12 }}>
                The clock is fixed once a status exists.
              </Text>
            )}
          </div>
          <div>
            <Text strong>Behaviour</Text>
            <Radio.Group
              value={behaviour}
              onChange={(e) => setBehaviour(e.target.value as BehaviourKey)}
              disabled={!!editing}
              style={{ display: 'block', marginTop: 4 }}
            >
              <Radio value='open'>{BEHAVIOUR_LABEL.open}</Radio>
              <Radio value='not_open'>{BEHAVIOUR_LABEL.not_open}</Radio>
              <Radio value='terminal'>{BEHAVIOUR_LABEL.terminal}</Radio>
            </Radio.Group>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {editing
                ? 'Behaviour is fixed once a status exists — changing it would re-scope every fund picker and the agent gate with no undo.'
                : BEHAVIOUR_HINT[behaviour]}
            </Text>
          </div>
          <div>
            <Text strong>Order</Text>
            <InputNumber
              value={sortValue ?? undefined}
              onChange={(v) => setSortValue(typeof v === 'number' ? v : null)}
              placeholder='Sort order'
              style={{ width: '100%', marginTop: 4 }}
            />
          </div>
        </Space>
      </Modal>

      <ReplaceFundStatusModal
        open={replaceOpen}
        statuses={rows}
        initialSourceIds={replaceInitialSources}
        confirmLoading={reassigning}
        onConfirm={onReassign}
        onCancel={() => setReplaceOpen(false)}
      />
    </div>
  )
}
